/**
 * Performance Utilities
 * Tools for monitoring and optimizing application performance
 */

/**
 * Measure component render time
 * @param {string} componentName - Name of component
 * @param {Function} callback - Function to measure
 */
export const measurePerformance = (componentName, callback) => {
  if (process.env.NODE_ENV === 'development') {
    const startTime = performance.now();
    const result = callback();
    const endTime = performance.now();
    console.log(`[Performance] ${componentName}: ${(endTime - startTime).toFixed(2)}ms`);
    return result;
  }
  return callback();
};

/**
 * Log Web Vitals metrics
 * @param {Object} metric - Web Vitals metric
 */
export const logWebVitals = (metric) => {
  const { name, value, id } = metric;

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Web Vitals] ${name}:`, value);
  }

  // Send to analytics in production
  if (process.env.NODE_ENV === 'production') {
    // Replace with your analytics endpoint
    // Example: gtag('event', name, { value, metric_id: id });
    console.log(`[Analytics] ${name}: ${value} (ID: ${id})`);
  }
};

/**
 * Debounce function for performance
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in ms
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait = 300) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function for performance
 * @param {Function} func - Function to throttle
 * @param {number} limit - Limit in ms
 * @returns {Function} Throttled function
 */
export const throttle = (func, limit = 100) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

/**
 * Lazy load image with Intersection Observer
 * @param {HTMLImageElement} img - Image element
 * @param {string} src - Image source
 */
export const lazyLoadImage = (img, src) => {
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          img.src = src;
          observer.unobserve(img);
        }
      });
    });
    observer.observe(img);
  } else {
    // Fallback for browsers without IntersectionObserver
    img.src = src;
  }
};

/**
 * Prefetch resource
 * @param {string} url - URL to prefetch
 * @param {string} as - Resource type (script, style, image, etc.)
 */
export const prefetch = (url, as = 'fetch') => {
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.as = as;
  link.href = url;
  document.head.appendChild(link);
};

/**
 * Preload critical resource
 * @param {string} url - URL to preload
 * @param {string} as - Resource type
 */
export const preload = (url, as = 'script') => {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = as;
  link.href = url;
  document.head.appendChild(link);
};

/**
 * Check if user prefers reduced motion
 * @returns {boolean}
 */
export const prefersReducedMotion = () => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Get connection speed
 * @returns {string} Connection type (slow-2g, 2g, 3g, 4g, unknown)
 */
export const getConnectionSpeed = () => {
  if ('connection' in navigator) {
    return navigator.connection.effectiveType || 'unknown';
  }
  return 'unknown';
};

/**
 * Check if device is low-end
 * @returns {boolean}
 */
export const isLowEndDevice = () => {
  if ('deviceMemory' in navigator) {
    return navigator.deviceMemory < 4; // Less than 4GB RAM
  }
  if ('hardwareConcurrency' in navigator) {
    return navigator.hardwareConcurrency < 4; // Less than 4 CPU cores
  }
  return false;
};

/**
 * Adaptive loading based on connection and device
 * @returns {Object} Loading strategy
 */
export const getLoadingStrategy = () => {
  const connectionSpeed = getConnectionSpeed();
  const isLowEnd = isLowEndDevice();
  const slowConnection = ['slow-2g', '2g'].includes(connectionSpeed);

  return {
    shouldLoadImages: !slowConnection,
    shouldLoadHeavyComponents: !isLowEnd && !slowConnection,
    shouldUseAnimations: !prefersReducedMotion() && !isLowEnd,
    imageQuality: slowConnection || isLowEnd ? 'low' : 'high',
    enablePrefetch: !slowConnection && !isLowEnd,
  };
};

/**
 * Memory usage monitor
 */
export const monitorMemory = () => {
  if (performance.memory) {
    const { usedJSHeapSize, totalJSHeapSize, jsHeapSizeLimit } = performance.memory;
    const usedMB = (usedJSHeapSize / 1024 / 1024).toFixed(2);
    const totalMB = (totalJSHeapSize / 1024 / 1024).toFixed(2);
    const limitMB = (jsHeapSizeLimit / 1024 / 1024).toFixed(2);

    console.log(`[Memory] Used: ${usedMB}MB / Total: ${totalMB}MB / Limit: ${limitMB}MB`);

    return {
      used: usedMB,
      total: totalMB,
      limit: limitMB,
      percentage: ((usedJSHeapSize / jsHeapSizeLimit) * 100).toFixed(2),
    };
  }
  return null;
};

/**
 * Request Idle Callback wrapper
 * @param {Function} callback - Function to execute
 * @param {Object} options - Options
 */
export const runWhenIdle = (callback, options = {}) => {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(callback, options);
  } else {
    setTimeout(callback, 0);
  }
};

/**
 * Batch DOM updates
 * @param {Function} callback - DOM update function
 */
export const batchDOMUpdates = (callback) => {
  requestAnimationFrame(() => {
    callback();
  });
};

export default {
  measurePerformance,
  logWebVitals,
  debounce,
  throttle,
  lazyLoadImage,
  prefetch,
  preload,
  prefersReducedMotion,
  getConnectionSpeed,
  isLowEndDevice,
  getLoadingStrategy,
  monitorMemory,
  runWhenIdle,
  batchDOMUpdates,
};
